import SideSheetModal from "@/components/SideSheetModal";
import Button from "@/components/Button";
import { useQuery } from "@apollo/client";
import PersonSideSheetContent from "./PersonSideSheetContent";
import PersonSideSheetSkeleton from "./PersonSideSheetSkeleton";
import { MaterialNames } from "@/components/material_symbol_names";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ccmsSuspensionsForStatus } from "@/features/person/utils/ccmsSuspensions";
import { usePersonProfileReadCapabilities } from "@/features/person/hooks/usePersonProfileReadCapabilities";
import {
  PERSON_SIDE_SHEET_CONTACT_QUERY,
  PERSON_SIDE_SHEET_EDUCATION_QUERY,
  PERSON_SIDE_SHEET_MEMBERSHIP_QUERY,
  PERSON_SIDE_SHEET_CAREER_QUERY,
  PERSON_SIDE_SHEET_GROUPS_QUERY,
  PERSON_SIDE_SHEET_QUERY
} from "./queries";
import { BackofficePerson } from "@/types";
import { partitionSideSheetActionButtons } from "./personSideSheetActions";

// Public types for PersonSideSheet
export interface CustomActionButton {
  text: string;
  variant?: "text" | "outlined" | "filled";
  onClick: () => void;
  testId?: string;
  iconName?: MaterialNames;
}

export type PersonSideSheetProps = {
  open: boolean;
  onClose: () => void;
  pgaId: string | null;
  title?: string;
  customActionButtons?: CustomActionButton[];
  showDefaultSections?: boolean;
  children?: React.ReactNode;
  // Optional sections - explicitly control what to show
  showPersonalDetails?: boolean;
  showEmployment?: boolean;
  showFacility?: boolean;
  showJobApplications?: boolean;
  // Alternate contact information (hidden by default, shown only if different)
  alternateEmail?: string;
  alternatePhone?: string;
  // University name for students (shows university instead of section)
  universityName?: string;
  /** When false, hides Open profile in the person heading (e.g. campus-only university staff). */
  showProfileActions?: boolean;
};

export default function PersonSideSheet({
  open,
  onClose,
  pgaId,
  title = "Person Details",
  customActionButtons,
  showDefaultSections = true,
  children,
  showPersonalDetails = false,
  showEmployment = false,
  showFacility = false,
  showJobApplications = false,
  alternateEmail,
  alternatePhone,
  universityName,
  showProfileActions = true
}: PersonSideSheetProps) {
  const {
    canViewPersonContact,
    canViewPersonMembership,
    canViewPersonEducation,
    canViewPersonCareer,
    canViewPersonGroups
  } = usePersonProfileReadCapabilities();

  const { data, loading, error } = useQuery(PERSON_SIDE_SHEET_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: !open || !pgaId
  });

  const skipMembershipQuery = !open || !pgaId || !canViewPersonMembership;
  const skipContactQuery = !open || !pgaId || !canViewPersonContact;
  const skipEducationQuery = !open || !pgaId || !canViewPersonEducation;

  const { data: membershipData, loading: membershipLoading } = useQuery(PERSON_SIDE_SHEET_MEMBERSHIP_QUERY, {
    variables: { pgaId },
    fetchPolicy: "network-only",
    skip: skipMembershipQuery
  });

  const { data: contactData, loading: contactLoading } = useQuery(PERSON_SIDE_SHEET_CONTACT_QUERY, {
    variables: { pgaId },
    fetchPolicy: "network-only",
    skip: skipContactQuery
  });

  const { data: educationData, loading: educationLoading } = useQuery(PERSON_SIDE_SHEET_EDUCATION_QUERY, {
    variables: { pgaId },
    fetchPolicy: "network-only",
    skip: skipEducationQuery
  });

  const skipCareerQuery = !open || !pgaId || !canViewPersonCareer;
  const { data: careerData, loading: careerLoading } = useQuery(PERSON_SIDE_SHEET_CAREER_QUERY, {
    variables: { pgaId },
    fetchPolicy: "network-only",
    skip: skipCareerQuery
  });
  const skipGroupsQuery = !open || !pgaId || !canViewPersonGroups;
  const { data: groupsData, loading: groupsLoading } = useQuery(PERSON_SIDE_SHEET_GROUPS_QUERY, {
    variables: { pgaId },
    fetchPolicy: "network-only",
    skip: skipGroupsQuery
  });
  const gatedQueriesLoading =
    (!skipMembershipQuery && membershipLoading) ||
    (!skipContactQuery && contactLoading) ||
    (!skipEducationQuery && educationLoading) ||
    (!skipCareerQuery && careerLoading) ||
    (!skipGroupsQuery && groupsLoading);

  function setIsSideSheetOpen(isOpen: boolean) {
    if (!isOpen) onClose();
  }

  const person: BackofficePerson | undefined = data?.person
    ? ({
        ...data.person,
        ...(membershipData?.person
          ? {
              latestProgram: membershipData.person.latestProgram,
              openCcmsSuspensions: membershipData.person.openCcmsSuspensions,
              ccmsSuspensions: ccmsSuspensionsForStatus(membershipData.person)
            }
          : {}),
        ...(contactData?.person ?? {}),
        ...(educationData?.person ?? {}),
        ...(careerData?.person?.employments !== undefined ? { employments: careerData.person.employments } : {}),
        ...(groupsData?.person?.groupAssignments !== undefined
          ? { groupAssignments: groupsData.person.groupAssignments }
          : {})
      } as BackofficePerson)
    : undefined;

  const footerPrimaryActions = partitionSideSheetActionButtons(customActionButtons);
  const showFooter = Boolean(pgaId && footerPrimaryActions.length > 0);

  const footerActions = showFooter ? (
    <>
      {footerPrimaryActions.map((button) => (
        <Button
          key={button.text}
          variant={button.variant ?? "filled"}
          icon={button.iconName}
          onClick={button.onClick}
          testId={button.testId}
        >
          {button.text}
        </Button>
      ))}
    </>
  ) : null;

  return (
    <SideSheetModal isSidebarOpen={open} setSidebarOpen={setIsSideSheetOpen} title={title} actions={footerActions}>
      {loading || gatedQueriesLoading ? (
        <PersonSideSheetSkeleton />
      ) : error ? (
        <ErrorBoundary variant="inline" title="Failed to Load Person" message={error.message} className="mt-6" />
      ) : person ? (
        <PersonSideSheetContent
          person={person}
          pgaId={pgaId as string}
          showDefaultSections={showDefaultSections}
          showPersonalDetails={showPersonalDetails}
          showEmployment={showEmployment}
          showFacility={showFacility}
          showJobApplications={showJobApplications}
          alternateEmail={alternateEmail}
          alternatePhone={alternatePhone}
          universityName={universityName}
          showContactSections={canViewPersonContact}
          showGroupsSection={canViewPersonGroups}
          showProfileActions={showProfileActions}
        >
          {children}
        </PersonSideSheetContent>
      ) : (
        <ErrorBoundary
          variant="fullPage"
          title="Person Not Found"
          message="The person you're looking for could not be found. They may not exist or you may not have permission to view their profile."
          className="mt-6"
        />
      )}
    </SideSheetModal>
  );
}
